/**
 * IssuesPage — EINE produktzentrierte, virtualisierte Arbeitsliste.
 * Facetten (Issue-Typ/Schweregrad) + Suche/Sort + Fix via Plan-012
 * ProductActionDrawer (lokaler State, Plan-012 unmodifiziert).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { Button, Input } from '@/components/v2/ui'
import { useWorkspaceIssues } from '@/hooks/useWorkspaceIssues'
import { useRecomputeWorkspace } from '@/hooks/useRecomputeWorkspace'
import { useIssuesFilters, type IssuesSort } from '@/hooks/useIssuesFilters'
import { IssuesFacetSidebar } from '@/components/v2/products/issues/IssuesFacetSidebar'
import { IssuesWorkList } from '@/components/v2/products/issues/IssuesWorkList'
import { ProductActionDrawer } from '@/components/v2/products/drawer'
import type { ProductWithIssues } from '@/lib/api/channel-readiness'

const SORTS: IssuesSort[] = ['count_desc', 'count_asc', 'name_asc']
const SORT_KEY: Record<IssuesSort, string> = {
  count_desc: 'sortCountDesc',
  count_asc: 'sortCountAsc',
  name_asc: 'sortNameAsc',
}

export function IssuesPage() {
  const { t } = useTranslation()
  const { totalIssues, isLoading, products = [] } = useWorkspaceIssues()
  const recompute = useRecomputeWorkspace()
  const f = useIssuesFilters(products)
  const [drawerProduct, setDrawerProduct] = useState<ProductWithIssues | null>(null)

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            {t('productsIssues.title')}
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {t('productsIssues.workList.summary', {
              products: products.length,
              violations: totalIssues,
            })}{' '}
            (<span data-testid="issues-total">{totalIssues}</span>)
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          data-testid="issues-recompute-btn"
          onClick={() => recompute.run()}
          disabled={recompute.isPending}
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${recompute.isPending ? 'animate-spin' : ''}`} />
          {recompute.isPending
            ? t('productsIssues.recomputePending')
            : t('productsIssues.recompute')}
        </Button>
      </header>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-6">
          <IssuesFacetSidebar
            typeCounts={f.typeCounts}
            severityCounts={f.severityCounts}
            activeTypes={f.activeTypes}
            activeSeverities={f.activeSeverities}
            onToggleType={f.toggleType}
            onToggleSeverity={f.toggleSeverity}
          />
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={f.search}
                onChange={(e) => f.setSearch(e.target.value)}
                placeholder={t('productsIssues.workList.searchPlaceholder')}
                data-testid="issues-search"
                className="max-w-xs"
              />
              <label className="flex items-center gap-1.5 text-sm text-neutral-500">
                {t('productsIssues.workList.sort')}
                <select
                  value={f.sort}
                  onChange={(e) => f.setSort(e.target.value as IssuesSort)}
                  data-testid="issues-sort"
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                >
                  {SORTS.map((s) => (
                    <option key={s} value={s}>
                      {t(`productsIssues.workList.${SORT_KEY[s]}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <IssuesWorkList products={f.filtered} onFix={setDrawerProduct} />
          </div>
        </div>
      )}

      <ProductActionDrawer
        open={!!drawerProduct}
        productId={drawerProduct?.product_id ?? null}
        productName={drawerProduct?.name ?? undefined}
        productSku={drawerProduct?.sku ?? undefined}
        thumbnailUrl={drawerProduct?.thumbnail_url ?? undefined}
        state={drawerProduct?.readiness_action_state}
        onClose={() => setDrawerProduct(null)}
      />
    </div>
  )
}
